import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as eudrApi from './api';

/**
 * Async thunk to generate EUDR report
 */
export const generateReport = createAsyncThunk(
  'eudr/generateReport',
  async (params, { rejectWithValue }) => {
    try {
      const response = await eudrApi.generateEudrReport(params);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error 
        || error.response?.data?.message 
        || error.message 
        || 'Failed to generate report';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to fetch reports for an account
 */
export const fetchReports = createAsyncThunk(
  'eudr/fetchReports',
  async (accountId, { rejectWithValue }) => {
    try {
      const response = await eudrApi.getEudrReports(accountId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Async thunk to delete a report
 */
export const deleteReport = createAsyncThunk(
  'eudr/deleteReport',
  async ({ reportId, accountId }, { rejectWithValue }) => {
    try {
      const response = await eudrApi.deleteEudrReport(reportId, accountId);
      return { reportId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Async thunk to check available dates
 */
export const checkAvailableDates = createAsyncThunk(
  'eudr/checkAvailableDates',
  async ({ latitude, longitude, zoom }, { rejectWithValue }) => {
    try {
      const response = await eudrApi.quickCheckDates(latitude, longitude, zoom);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  reports: [],
  currentReport: null,
  lastGeneratedReport: null, // Store last generated report for localStorage
  availableDates: [],
  loading: false,
  generating: false,
  error: null,
  success: null
};

const eudrSlice = createSlice({
  name: 'eudr',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    resetEudrState: (state) => {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      // Generate Report
      .addCase(generateReport.pending, (state) => {
        state.generating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(generateReport.fulfilled, (state, action) => {
        state.generating = false;
        state.currentReport = action.payload;
        state.lastGeneratedReport = action.payload; // Store for localStorage
        state.reports.unshift(action.payload);
        state.success = 'Report generated successfully!';
      })
      .addCase(generateReport.rejected, (state, action) => {
        state.generating = false;
        state.error = action.payload || 'Failed to generate report';
      })
      
      // Fetch Reports
      .addCase(fetchReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReports.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both array and object responses
        if (Array.isArray(action.payload)) {
          state.reports = action.payload;
        } else if (action.payload?.reports) {
          state.reports = action.payload.reports;
        } else {
          state.reports = [];
        }
      })
      .addCase(fetchReports.rejected, (state, action) => {
        state.loading = false;
        state.reports = []; // Set to empty array on error
        console.warn('Failed to fetch reports:', action.payload);
        // Don't show error to user if no reports exist yet
      })
      
      // Delete Report
      .addCase(deleteReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteReport.fulfilled, (state, action) => {
        state.loading = false;
        state.reports = state.reports.filter(r => r.reportId !== action.payload.reportId);
        state.success = 'Report deleted successfully!';
      })
      .addCase(deleteReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete report';
      })
      
      // Check Available Dates
      .addCase(checkAvailableDates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkAvailableDates.fulfilled, (state, action) => {
        state.loading = false;
        state.availableDates = action.payload.availableDates || [];
      })
      .addCase(checkAvailableDates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to check dates';
      });
  }
});

export const { clearError, clearSuccess, resetEudrState } = eudrSlice.actions;
export default eudrSlice.reducer;
